import { ReactElement, ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DATA_SCRIPT_ID_ATTRIBUTE_KEY, useExternalLink } from '@breathelife/react-hooks';
import { Button, Loader } from '@breathelife/ui-components';

import { useAuraNextMedicalInterviewContext } from '../../../../../../Context/AuraNextMedicalInterviewContextProvider';
import { useFullPageLoaderContext } from '../../../../../LoadingView/FullPageLoader';
import { AssistedApplicationLayout } from '../../../../AssistedApplicationLayout/AssistedApplicationLayout';
import { HeaderButtonContainer, HeaderTitleContainer, HeaderWrapper } from '../../../Styles';
import {
  AuraNextForm,
  CustomSubmitButton,
  ErrorContainer,
  ErrorMessage,
  ErrorTitle,
  HeaderTitle,
  LoaderContainer,
} from './Styles';

type AuraNextFormContainerProps = {
  participant: {
    id: string;
    name?: string;
    applicationId: string;
  };
};

export function AuraNextFormContainer({ participant }: AuraNextFormContainerProps): ReactElement {
  const { t } = useTranslation();
  const {
    isEnabled,
    config,
    saveOrSubmitInterviewToProvider,
    isSavingInterview,
    isStartingInterview,
    error,
    startInterview,
    canSubmitInterview,
  } = useAuraNextMedicalInterviewContext();
  const [isRestarting, setIsRestarting] = useState(false);
  const { isOpen } = useFullPageLoaderContext();

  const handleRestartInterview = useCallback(async () => {
    setIsRestarting(true);
    try {
      await startInterview(participant.id);
    } finally {
      setTimeout(() => setIsRestarting(false), 1000);
    }
  }, [startInterview, participant.id]);

  const { isLoaded: isDeuiCssLoaded } = useExternalLink([
    {
      id: 'aura-next-disclosures-css',
      href: `${config?.deuiAppConfigdeUrl}/deui/app.css` || '',
      enabled: !!isEnabled,
    },
  ]);

  const removeExternalLink = useCallback((id: string) => {
    const existingLinkElement = document.querySelector(`link[${DATA_SCRIPT_ID_ATTRIBUTE_KEY}=${id}]`);

    if (existingLinkElement) {
      document.head.removeChild(existingLinkElement);
    }
  }, []);

  return isDeuiCssLoaded ? (
    <AssistedApplicationLayout>
      <AssistedApplicationLayout.Header>
        <HeaderWrapper>
          <HeaderTitleContainer>
            <HeaderTitle>{participant?.name}</HeaderTitle>
          </HeaderTitleContainer>
          <HeaderButtonContainer>
            {!isStartingInterview && (
              <AuraNextSubmitButton
                onClick={() => {
                  saveOrSubmitInterviewToProvider();
                  removeExternalLink('aura-next-disclosures-css');
                }}
                disabled={isStartingInterview || isSavingInterview || !!error}
              >
                {isOpen ? (
                  <Loader size='20px' color='white' />
                ) : canSubmitInterview ? (
                  t('assistedApplication.auraNextMedicalQuestionnaireSubsection.submitAndExit')
                ) : (
                  t('assistedApplication.auraNextMedicalQuestionnaireSubsection.saveAndClose')
                )}
              </AuraNextSubmitButton>
            )}
          </HeaderButtonContainer>
        </HeaderWrapper>
      </AssistedApplicationLayout.Header>
      <AssistedApplicationLayout.Content>
        {/* Show loader while starting interview */}
        {isStartingInterview && (
          <LoaderContainer>
            <Loader />
          </LoaderContainer>
        )}

        {/* Show form when interview is ready and no error */}
        <AuraNextForm
          id='aura-next-form'
          role='form'
          style={{ display: error || isStartingInterview ? 'none' : 'block' }}
        >
          <Loader />
        </AuraNextForm>

        {/* Show error messages only when not starting */}
        {!isStartingInterview && error === 'authenticationError' && (
          <ErrorContainer>
            <ErrorTitle>{t('assistedApplication.auraNext.error.expired')}</ErrorTitle>
            <ErrorMessage>{t('assistedApplication.auraNext.error.whatToDo')}</ErrorMessage>
            <Button type='reset' onClick={handleRestartInterview} disabled={isRestarting} loading={isRestarting}>
              {t('assistedApplication.auraNext.error.continueButton')}
            </Button>
          </ErrorContainer>
        )}

        {!isStartingInterview && error === 'unknownError' && (
          <ErrorContainer>
            <ErrorTitle>{t('assistedApplication.auraNext.error.unknown')}</ErrorTitle>
            <ErrorMessage>{t('assistedApplication.auraNext.error.whatToDo')}</ErrorMessage>
            <Button type='reset' onClick={handleRestartInterview} disabled={isRestarting} loading={isRestarting}>
              {t('assistedApplication.auraNext.error.continueButton')}
            </Button>
          </ErrorContainer>
        )}
      </AssistedApplicationLayout.Content>
    </AssistedApplicationLayout>
  ) : (
    <Loader />
  );
}

export function AuraNextSubmitButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}): ReactElement {
  const handleClick = useCallback(() => {
    if (!disabled) {
      onClick();
    }
  }, [disabled, onClick]);

  return (
    <CustomSubmitButton type='button' onClick={handleClick} disabled={disabled}>
      {children}
    </CustomSubmitButton>
  );
}
